import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { formatINR, stripCommas } from '../lib/numberFormat';
import {
  Calculator, ArrowLeft, RefreshCw, Shield, AlertTriangle,
  TrendingUp, Percent, IndianRupee, ArrowRight, Check, X,
  Zap, Info, ChevronRight, Lock, Scale, PiggyBank
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Animated Counter Component
const AnimatedCounter = ({ value, prefix = '', suffix = '', decimals = 0, duration = 1500 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const startTimeRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    startTimeRef.current = null;
    
    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const progress = Math.min((timestamp - startTimeRef.current) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(value * easeOutQuart);
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, duration]);

  const formattedValue = displayValue.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

  return <span>{prefix}{formattedValue}{suffix}</span>;
};

// Speedometer Component for LTV
const Speedometer = ({ value }) => {
  const getColor = (val) => {
    if (val <= 60) return 'text-green-500';
    if (val <= 75) return 'text-amber-500';
    return 'text-red-500';
  };

  const getZone = (val) => {
    if (val <= 60) return { label: 'Safe Zone', color: 'bg-green-500', textColor: 'text-green-500' };
    if (val <= 75) return { label: 'Caution Zone', color: 'bg-amber-500', textColor: 'text-amber-500' };
    return { label: 'Danger Zone', color: 'bg-red-500', textColor: 'text-red-500' };
  };

  const zone = getZone(value);
  const rotation = Math.min(value, 100) * 1.8 - 90; // Map 0-100 to -90 to 90 degrees

  return (
    <div className="relative w-48 h-24 mx-auto">
      {/* Background arc */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="w-48 h-48 rounded-full border-[16px] border-slate-700" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)' }}></div>
      </div>
      
      {/* Colored zones */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="w-48 h-48 rounded-full border-[16px]" 
          style={{ 
            clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
            borderColor: value <= 60 ? '#22c55e' : value <= 75 ? '#f59e0b' : '#ef4444'
          }}
        ></div>
      </div>

      {/* Needle */}
      <div 
        className="absolute bottom-0 left-1/2 w-1 h-20 bg-white rounded-full origin-bottom transform -translate-x-1/2 transition-transform duration-1000"
        style={{ transform: `translateX(-50%) rotate(${rotation}deg)` }}
      >
        <div className="absolute top-0 left-1/2 w-3 h-3 bg-white rounded-full transform -translate-x-1/2 -translate-y-1"></div>
      </div>

      {/* Center point */}
      <div className="absolute bottom-0 left-1/2 w-4 h-4 bg-slate-800 border-2 border-white rounded-full transform -translate-x-1/2 translate-y-1/2"></div>

      {/* Value display */}
      <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-center">
        <p className={`text-3xl font-bold ${zone.textColor}`}>{value.toFixed(1)}%</p>
        <p className={`text-sm font-medium ${zone.textColor}`}>{zone.label}</p>
      </div>
    </div>
  );
};

const Calculator_Tool = () => {
  const [activeTab, setActiveTab] = useState('vaddi');
  const [showResult, setShowResult] = useState(false);
  const [goldPrice, setGoldPrice] = useState({ price_22k: 6870, price_24k: 7500 });
  const [loadingPrice, setLoadingPrice] = useState(false);

  // Vaddi Decoder State
  const [vaddiForm, setVaddiForm] = useState({
    loanAmount: '',
    interestType: 'rupees', // 'rupees' or 'percent'
    interestValue: ''
  });
  const [vaddiResult, setVaddiResult] = useState(null);

  // LTV Health Check State
  const [ltvForm, setLtvForm] = useState({
    assetType: 'gold',
    goldWeight: '',
    goldPurity: '22k',
    assetValue: '',
    loanAmount: '',
    useManualPrice: false,
    manualGoldPrice: ''
  });
  const [ltvResult, setLtvResult] = useState(null);

  // Refinance Saver State
  const [refinanceForm, setRefinanceForm] = useState({
    loanAmount: '',
    currentRate: '',
    newRate: '10.5'
  });
  const [refinanceResult, setRefinanceResult] = useState(null);

  useEffect(() => {
    fetchGoldPrice();
  }, []);

  const fetchGoldPrice = async () => {
    setLoadingPrice(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/gold-price`);
      setGoldPrice(res.data);
    } catch (err) {
      // Use fallback values
    } finally {
      setLoadingPrice(false);
    }
  };

  // Vaddi Decoder Calculation
  const calculateVaddi = () => {
    const loan = parseFloat(vaddiForm.loanAmount) || 0;
    const interest = parseFloat(vaddiForm.interestValue) || 0;

    if (loan <= 0 || interest <= 0) {
      toast.error('Please enter valid loan amount and interest');
      return;
    }

    let annualRate, monthlyInterest, yearlyInterest;

    if (vaddiForm.interestType === 'rupees') {
      // Rupees interest per 100 per month
      annualRate = interest * 12;
      monthlyInterest = (loan * interest) / 100;
      yearlyInterest = monthlyInterest * 12;
    } else {
      // Percentage per annum
      annualRate = interest;
      yearlyInterest = (loan * interest) / 100;
      monthlyInterest = yearlyInterest / 12;
    }

    setVaddiResult({
      annualRate,
      monthlyInterest,
      yearlyInterest,
      loanAmount: loan,
      isExpensive: annualRate > 18,
      isGood: annualRate < 12
    });
    setShowResult(true);
  };

  // LTV Health Check Calculation
  const calculateLTV = () => {
    let assetValue = parseFloat(ltvForm.assetValue) || 0;
    const loanAmount = parseFloat(ltvForm.loanAmount) || 0;

    if (ltvForm.assetType === 'gold' && ltvForm.goldWeight) {
      const weight = parseFloat(ltvForm.goldWeight) || 0;
      const pricePerGram = ltvForm.useManualPrice 
        ? parseFloat(ltvForm.manualGoldPrice) || 0
        : (ltvForm.goldPurity === '22k' ? goldPrice.price_22k : goldPrice.price_24k);
      assetValue = weight * pricePerGram;
    }

    if (assetValue <= 0 || loanAmount <= 0) {
      toast.error('Please enter valid asset value and loan amount');
      return;
    }

    const ltv = (loanAmount / assetValue) * 100;
    const equity = assetValue - loanAmount;

    setLtvResult({
      ltv,
      assetValue,
      loanAmount,
      equity,
      isSafe: ltv <= 60,
      isCaution: ltv > 60 && ltv <= 75,
      isDanger: ltv > 75
    });
    setShowResult(true);
  };

  // Refinance Saver Calculation
  const calculateRefinance = () => {
    const loan = parseFloat(refinanceForm.loanAmount) || 0;
    const currentRate = parseFloat(refinanceForm.currentRate) || 0;
    const newRate = parseFloat(refinanceForm.newRate) || 10.5;

    if (loan <= 0 || currentRate <= 0) {
      toast.error('Please enter valid loan amount and current rate');
      return;
    }

    const currentYearlyCost = (loan * currentRate) / 100;
    const newYearlyCost = (loan * newRate) / 100;
    const savings = currentYearlyCost - newYearlyCost;

    setRefinanceResult({
      currentYearlyCost,
      newYearlyCost,
      savings,
      loanAmount: loan,
      currentRate,
      newRate,
      hasSavings: savings > 0
    });
    setShowResult(true);
  };

  // Pre-fill refinance from vaddi result
  const useVaddiForRefinance = () => {
    if (vaddiResult) {
      setRefinanceForm({
        ...refinanceForm,
        loanAmount: vaddiResult.loanAmount.toString(),
        currentRate: vaddiResult.annualRate.toString()
      });
      setActiveTab('refinance');
      setShowResult(false);
    }
  };

  const resetCalculator = () => {
    setShowResult(false);
    setVaddiResult(null);
    setLtvResult(null);
    setRefinanceResult(null);
  };

  const tabs = [
    { id: 'vaddi', label: 'Vaddi Decoder', icon: Percent, color: 'blue' },
    { id: 'ltv', label: 'LTV Health', icon: Scale, color: 'amber' },
    { id: 'refinance', label: 'Refinance Saver', icon: PiggyBank, color: 'green' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors">
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Back to Home</span>
            </Link>
            <div className="flex items-center gap-2">
              <Calculator className="text-amber-400" size={24} />
              <span className="font-bold text-white">Wealth Diagnostics</span>
            </div>
            <a href="/login" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 text-sm font-medium">
              Login →
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Calculate your <span className="text-amber-400">True Loan Cost</span>
          </h1>
          <p className="text-slate-400">Free tools to understand your secured loans better</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); resetCalculator(); }}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-amber-500 text-slate-900'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Calculator Card */}
        <div className="bg-slate-800/80 backdrop-blur border border-slate-700 rounded-2xl p-6 sm:p-8">
          
          {/* Vaddi Decoder Tab */}
          {activeTab === 'vaddi' && (
            <div>
              <div className="flex items-start gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Percent className="text-blue-400" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">The "Vaddi" Decoder</h2>
                  <p className="text-slate-400 text-sm">Convert "Rupees Interest" to standard % per annum</p>
                </div>
              </div>

              {!showResult ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Loan Amount</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                      <input
                        type="text" inputMode="decimal"
                        placeholder="e.g., 100000"
                        value={formatINR(vaddiForm.loanAmount)}
                        onChange={(e) => setVaddiForm({ ...vaddiForm, loanAmount: stripCommas(e.target.value) })}
                        className="w-full pl-10 pr-4 py-4 bg-slate-700/50 border border-slate-600 rounded-xl text-white text-lg placeholder-slate-500 focus:outline-none focus:border-amber-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Interest Type</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setVaddiForm({ ...vaddiForm, interestType: 'rupees' })}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          vaddiForm.interestType === 'rupees'
                            ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                            : 'border-slate-600 text-slate-300 hover:border-slate-500'
                        }`}
                      >
                        <IndianRupee size={24} className="mx-auto mb-2" />
                        <p className="font-medium">₹ Rupees</p>
                        <p className="text-xs text-slate-400">per 100 per month</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setVaddiForm({ ...vaddiForm, interestType: 'percent' })}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          vaddiForm.interestType === 'percent'
                            ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                            : 'border-slate-600 text-slate-300 hover:border-slate-500'
                        }`}
                      >
                        <Percent size={24} className="mx-auto mb-2" />
                        <p className="font-medium">% Percent</p>
                        <p className="text-xs text-slate-400">per annum</p>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Interest Value {vaddiForm.interestType === 'rupees' ? '(₹ per 100/month)' : '(% p.a.)'}
                    </label>
                    <input
                      type="text" inputMode="decimal"
                     
                      placeholder={vaddiForm.interestType === 'rupees' ? 'e.g., 1.50 or 2' : 'e.g., 12'}
                      value={formatINR(vaddiForm.interestValue)}
                      onChange={(e) => setVaddiForm({ ...vaddiForm, interestValue: stripCommas(e.target.value) })}
                      className="w-full px-4 py-4 bg-slate-700/50 border border-slate-600 rounded-xl text-white text-lg placeholder-slate-500 focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <button
                    onClick={calculateVaddi}
                    className="w-full py-4 bg-amber-500 text-slate-900 font-semibold rounded-xl hover:bg-amber-400 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
                  >
                    <Calculator size={20} />
                    Calculate True Cost
                  </button>
                </div>
              ) : vaddiResult && (
                <div className="space-y-6">
                  {/* Result Spotlight */}
                  <div className={`p-8 rounded-2xl text-center ${
                    vaddiResult.isGood ? 'bg-green-500/10 border border-green-500/30' :
                    vaddiResult.isExpensive ? 'bg-red-500/10 border border-red-500/30' :
                    'bg-amber-500/10 border border-amber-500/30'
                  }`}>
                    <p className="text-slate-400 mb-2">Your True Interest Rate is</p>
                    <p className={`text-5xl font-bold mb-2 ${
                      vaddiResult.isGood ? 'text-green-400' :
                      vaddiResult.isExpensive ? 'text-red-400' : 'text-amber-400'
                    }`}>
                      <AnimatedCounter value={vaddiResult.annualRate} suffix="%" decimals={1} /> p.a.
                    </p>
                    <p className="text-slate-300">
                      You are paying <span className="font-bold text-white">₹<AnimatedCounter value={vaddiResult.yearlyInterest} decimals={0} /></span> in interest every year
                    </p>
                    
                    <div className="mt-4 flex items-center justify-center gap-2">
                      {vaddiResult.isGood ? (
                        <><Shield className="text-green-400" size={24} /><span className="text-green-400 font-medium">Great Rate!</span></>
                      ) : vaddiResult.isExpensive ? (
                        <><AlertTriangle className="text-red-400" size={24} /><span className="text-red-400 font-medium">Very Expensive!</span></>
                      ) : (
                        <><Info className="text-amber-400" size={24} /><span className="text-amber-400 font-medium">Average Rate</span></>
                      )}
                    </div>
                  </div>

                  {/* Breakdown */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-700/30 rounded-xl p-4">
                      <p className="text-slate-400 text-sm">Monthly Interest</p>
                      <p className="text-xl font-bold text-white">₹<AnimatedCounter value={vaddiResult.monthlyInterest} decimals={0} /></p>
                    </div>
                    <div className="bg-slate-700/30 rounded-xl p-4">
                      <p className="text-slate-400 text-sm">Yearly Interest</p>
                      <p className="text-xl font-bold text-white">₹<AnimatedCounter value={vaddiResult.yearlyInterest} decimals={0} /></p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={useVaddiForRefinance}
                      className="flex-1 py-3 bg-green-500/20 border border-green-500/30 text-green-400 font-medium rounded-xl hover:bg-green-500/30 transition-colors flex items-center justify-center gap-2"
                    >
                      <PiggyBank size={18} />
                      Check if you can save money?
                    </button>
                    <button
                      onClick={resetCalculator}
                      className="py-3 px-6 bg-slate-700 text-slate-300 font-medium rounded-xl hover:bg-slate-600 transition-colors"
                    >
                      Calculate Again
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* LTV Health Check Tab */}
          {activeTab === 'ltv' && (
            <div>
              <div className="flex items-start gap-3 mb-6">
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Scale className="text-amber-400" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">LTV Health Check</h2>
                  <p className="text-slate-400 text-sm">Is your Gold/Stock loan safe from auction?</p>
                </div>
              </div>

              {!showResult ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Asset Type</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setLtvForm({ ...ltvForm, assetType: 'gold' })}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          ltvForm.assetType === 'gold'
                            ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                            : 'border-slate-600 text-slate-300 hover:border-slate-500'
                        }`}
                      >
                        <span className="text-2xl mb-2 block">🪙</span>
                        <p className="font-medium">Gold</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setLtvForm({ ...ltvForm, assetType: 'stocks' })}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          ltvForm.assetType === 'stocks'
                            ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                            : 'border-slate-600 text-slate-300 hover:border-slate-500'
                        }`}
                      >
                        <span className="text-2xl mb-2 block">📈</span>
                        <p className="font-medium">Stocks</p>
                      </button>
                    </div>
                  </div>

                  {ltvForm.assetType === 'gold' && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Gold Weight (grams)</label>
                          <input
                            type="number"
                            placeholder="e.g., 50"
                            value={ltvForm.goldWeight}
                            onChange={(e) => setLtvForm({ ...ltvForm, goldWeight: e.target.value })}
                            className="w-full px-4 py-4 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Purity</label>
                          <select
                            value={ltvForm.goldPurity}
                            onChange={(e) => setLtvForm({ ...ltvForm, goldPurity: e.target.value })}
                            className="w-full px-4 py-4 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:border-amber-500"
                          >
                            <option value="22k">22K (Jewellery)</option>
                            <option value="24k">24K (Pure)</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-xl">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="manualPrice"
                            checked={ltvForm.useManualPrice}
                            onChange={(e) => setLtvForm({ ...ltvForm, useManualPrice: e.target.checked })}
                            className="w-4 h-4 rounded border-slate-500"
                          />
                          <label htmlFor="manualPrice" className="text-sm text-slate-300">Use manual gold price</label>
                        </div>
                        {!ltvForm.useManualPrice && (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-slate-400">Live: ₹{goldPrice.price_22k}/g (22K)</span>
                            <button onClick={fetchGoldPrice} className="text-amber-400 hover:text-amber-300">
                              <RefreshCw size={14} className={loadingPrice ? 'animate-spin' : ''} />
                            </button>
                          </div>
                        )}
                      </div>

                      {ltvForm.useManualPrice && (
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Gold Price (₹ per gram)</label>
                          <input
                            type="text" inputMode="decimal"
                            placeholder="e.g., 6500"
                            value={formatINR(ltvForm.manualGoldPrice)}
                            onChange={(e) => setLtvForm({ ...ltvForm, manualGoldPrice: stripCommas(e.target.value) })}
                            className="w-full px-4 py-4 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                          />
                        </div>
                      )}
                    </>
                  )}

                  {ltvForm.assetType === 'stocks' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Current Value of Stocks</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                        <input
                          type="text" inputMode="decimal"
                          placeholder="e.g., 500000"
                          value={formatINR(ltvForm.assetValue)}
                          onChange={(e) => setLtvForm({ ...ltvForm, assetValue: stripCommas(e.target.value) })}
                          className="w-full pl-10 pr-4 py-4 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Outstanding Loan Amount</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                      <input
                        type="text" inputMode="decimal"
                        placeholder="e.g., 300000"
                        value={formatINR(ltvForm.loanAmount)}
                        onChange={(e) => setLtvForm({ ...ltvForm, loanAmount: stripCommas(e.target.value) })}
                        className="w-full pl-10 pr-4 py-4 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                      />
                    </div>
                  </div>

                  <button
                    onClick={calculateLTV}
                    className="w-full py-4 bg-amber-500 text-slate-900 font-semibold rounded-xl hover:bg-amber-400 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
                  >
                    <Scale size={20} />
                    Check LTV Health
                  </button>
                </div>
              ) : ltvResult && (
                <div className="space-y-6">
                  {/* Speedometer */}
                  <div className="py-8">
                    <Speedometer value={ltvResult.ltv} />
                  </div>

                  {/* Zone Explanation */}
                  <div className={`p-6 rounded-2xl ${
                    ltvResult.isSafe ? 'bg-green-500/10 border border-green-500/30' :
                    ltvResult.isCaution ? 'bg-amber-500/10 border border-amber-500/30' :
                    'bg-red-500/10 border border-red-500/30'
                  }`}>
                    <div className="flex items-center gap-3 mb-3">
                      {ltvResult.isSafe ? <Shield className="text-green-400" size={28} /> :
                       ltvResult.isCaution ? <AlertTriangle className="text-amber-400" size={28} /> :
                       <X className="text-red-400" size={28} />}
                      <span className={`text-xl font-bold ${
                        ltvResult.isSafe ? 'text-green-400' :
                        ltvResult.isCaution ? 'text-amber-400' : 'text-red-400'
                      }`}>
                        {ltvResult.isSafe ? 'Your asset is secure!' :
                         ltvResult.isCaution ? 'Price drop could trigger risk' :
                         'High risk of margin call/auction!'}
                      </span>
                    </div>
                    <p className="text-slate-300">
                      You still own <span className="font-bold text-white">₹<AnimatedCounter value={ltvResult.equity} decimals={0} /></span> of this asset. Keep it safe!
                    </p>
                  </div>

                  {/* Breakdown */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-700/30 rounded-xl p-4">
                      <p className="text-slate-400 text-sm">Asset Value</p>
                      <p className="text-xl font-bold text-white">₹<AnimatedCounter value={ltvResult.assetValue} decimals={0} /></p>
                    </div>
                    <div className="bg-slate-700/30 rounded-xl p-4">
                      <p className="text-slate-400 text-sm">Loan Amount</p>
                      <p className="text-xl font-bold text-white">₹<AnimatedCounter value={ltvResult.loanAmount} decimals={0} /></p>
                    </div>
                  </div>

                  <button
                    onClick={resetCalculator}
                    className="w-full py-3 bg-slate-700 text-slate-300 font-medium rounded-xl hover:bg-slate-600 transition-colors"
                  >
                    Calculate Again
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Refinance Saver Tab */}
          {activeTab === 'refinance' && (
            <div>
              <div className="flex items-start gap-3 mb-6">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <PiggyBank className="text-green-400" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">The Refinance Saver</h2>
                  <p className="text-slate-400 text-sm">Should you switch your loan to save money?</p>
                </div>
              </div>

              {!showResult ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Current Loan Amount</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                      <input
                        type="text" inputMode="decimal"
                        placeholder="e.g., 100000"
                        value={formatINR(refinanceForm.loanAmount)}
                        onChange={(e) => setRefinanceForm({ ...refinanceForm, loanAmount: stripCommas(e.target.value) })}
                        className="w-full pl-10 pr-4 py-4 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Current Interest Rate (% p.a.)</label>
                    <input
                      type="text" inputMode="decimal"
                     
                      placeholder="e.g., 24"
                      value={formatINR(refinanceForm.currentRate)}
                      onChange={(e) => setRefinanceForm({ ...refinanceForm, currentRate: stripCommas(e.target.value) })}
                      className="w-full px-4 py-4 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">New Bank Offer Rate (% p.a.)</label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="e.g., 10.5"
                      value={refinanceForm.newRate}
                      onChange={(e) => setRefinanceForm({ ...refinanceForm, newRate: e.target.value })}
                      className="w-full px-4 py-4 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                    />
                    <p className="text-xs text-slate-500 mt-2">Average bank gold loan rate is ~10.5% p.a.</p>
                  </div>

                  <button
                    onClick={calculateRefinance}
                    className="w-full py-4 bg-amber-500 text-slate-900 font-semibold rounded-xl hover:bg-amber-400 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
                  >
                    <PiggyBank size={20} />
                    Calculate Savings
                  </button>
                </div>
              ) : refinanceResult && (
                <div className="space-y-6">
                  {/* Savings Spotlight */}
                  <div className={`p-8 rounded-2xl text-center ${
                    refinanceResult.hasSavings
                      ? 'bg-green-500/10 border border-green-500/30'
                      : 'bg-slate-700/30 border border-slate-600'
                  }`}>
                    {refinanceResult.hasSavings ? (
                      <>
                        <p className="text-slate-400 mb-2">You could save</p>
                        <p className="text-5xl font-bold text-green-400 mb-2">
                          ₹<AnimatedCounter value={refinanceResult.savings} decimals={0} />
                        </p>
                        <p className="text-slate-300">every year by refinancing!</p>
                        <div className="mt-4 flex items-center justify-center gap-2">
                          <Zap className="text-green-400" size={24} />
                          <span className="text-green-400 font-medium">Great opportunity to save!</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-slate-400 mb-2">Current rate is better</p>
                        <p className="text-3xl font-bold text-white mb-2">No savings available</p>
                        <p className="text-slate-400">Your current rate is already competitive.</p>
                      </>
                    )}
                  </div>

                  {/* Comparison */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                      <p className="text-slate-400 text-sm">Current Yearly Cost</p>
                      <p className="text-xl font-bold text-red-400">₹<AnimatedCounter value={refinanceResult.currentYearlyCost} decimals={0} /></p>
                      <p className="text-xs text-slate-500">@ {refinanceResult.currentRate}% p.a.</p>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                      <p className="text-slate-400 text-sm">New Yearly Cost</p>
                      <p className="text-xl font-bold text-green-400">₹<AnimatedCounter value={refinanceResult.newYearlyCost} decimals={0} /></p>
                      <p className="text-xs text-slate-500">@ {refinanceResult.newRate}% p.a.</p>
                    </div>
                  </div>

                  {refinanceResult.hasSavings && (
                    <Link
                      to="/#waitlist"
                      className="block w-full py-4 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-400 transition-all text-center"
                    >
                      Join Waitlist to Switch & Save
                    </Link>
                  )}

                  <button
                    onClick={resetCalculator}
                    className="w-full py-3 bg-slate-700 text-slate-300 font-medium rounded-xl hover:bg-slate-600 transition-colors"
                  >
                    Calculate Again
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="mt-8 text-center">
          <p className="text-slate-400 mb-4">Want real-time monitoring for all your loans?</p>
          <Link
            to="/#waitlist"
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500/20 border border-amber-500/30 text-amber-400 rounded-xl hover:bg-amber-500/30 transition-colors"
          >
            Join Maa Dabbu Waitlist
            <ArrowRight size={18} />
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
              <span className="text-slate-900 font-bold">M</span>
            </div>
            <span className="font-semibold text-white">Maa Dabbu</span>
          </div>
          <p className="text-slate-500 text-sm flex items-center justify-center gap-2">
            <Lock size={14} />
            Your calculations are private. We don't store any data.
          </p>
          <p className="text-slate-600 text-xs mt-4">© 2025 Maa Dabbu Technologies</p>
        </div>
      </footer>
    </div>
  );
};

export default Calculator_Tool;
